/**
 * Post-insertion pass that applies inline color / background highlights
 * to evidence-strength markers and source-status badges (Strong /
 * Moderate / Weak / Insufficient / Verified / Preprint / Retracted)
 * inside the freshly-inserted synthesis blocks.
 *
 * Why post-insertion? BlockSuite's markdown adapter doesn't round-trip
 * `color` or `background` inline attributes (and `==highlight==` is
 * stripped). Those attributes ARE available on Yjs Text, though — we
 * just have to apply them via `text.format(index, length, attrs)`
 * after the markdown adapter has produced the block models.
 *
 * Detection strategy:
 *   - For evidence-strength words (Strong / Moderate / …), we only
 *     highlight when the run was already bold (i.e. the LLM emitted
 *     `**Strong**`). This avoids colouring the word "strong" when it
 *     appears in regular prose.
 *   - For status badges (Verified / Preprint / Retracted), we match
 *     them anywhere — they almost only occur in the Sources block we
 *     control, and the false-positive risk is tolerably low.
 *
 * The pass walks the model tree (callout children, list items, etc.)
 * so highlights land regardless of which block flavour holds the text.
 */
import type { Store } from '@blocksuite/affine/store';

interface HighlightStyle {
  color?: string;
  background?: string;
  strike?: true | null;
}

// Mature, low-saturation palette: same family as a Linear / Notion tag.
const STRENGTH_STYLES: Record<string, HighlightStyle> = {
  strong: { color: '#15803d', background: '#dcfce7' },
  moderate: { color: '#92400e', background: '#fef3c7' },
  weak: { color: '#9a3412', background: '#ffedd5' },
  'very weak': { color: '#991b1b', background: '#fee2e2' },
  insufficient: { color: '#991b1b', background: '#fee2e2' },
};

const STATUS_BADGE_STYLES: Record<string, HighlightStyle> = {
  verified: { color: '#15803d', background: '#dcfce7' },
  unverified: { color: '#b45309', background: '#fef3c7' },
  preprint: { color: '#854d0e', background: '#fef9c3' },
  retracted: { color: '#991b1b', background: '#fee2e2', strike: true },
  'low confidence': { color: '#475569', background: '#f1f5f9' },
};

const BADGE_PATTERN =
  /\b(Unverified|Verified|Preprint|Retracted|Low confidence)\b/g;

interface YText {
  toString(): string;
  toDelta?(): Array<{ insert?: unknown; attributes?: Record<string, unknown> }>;
  format(index: number, length: number, attrs: Record<string, unknown>): void;
}

interface BlockLike {
  flavour: string;
  text?: YText;
  children?: BlockLike[];
}

function walk(model: BlockLike, visit: (m: BlockLike) => void): void {
  visit(model);
  for (const child of model.children ?? []) {
    walk(child, visit);
  }
}

/**
 * Highlight all bold runs whose plain text matches an evidence-grade
 * word. Iterating the Y.Text delta lets us check the `bold` attribute
 * cheaply and compute the run's absolute offset by accumulation.
 */
function highlightEvidenceGrades(text: YText): void {
  const delta = text.toDelta?.();
  if (!Array.isArray(delta)) return;
  let offset = 0;
  for (const op of delta) {
    const insertVal = op.insert;
    if (typeof insertVal !== 'string') {
      // Non-string inserts (embeds) still consume one position.
      offset += 1;
      continue;
    }
    const len = insertVal.length;
    const isBold = op.attributes?.bold === true;
    if (isBold) {
      const word = insertVal
        .trim()
        .toLowerCase()
        .replace(/^[()]+|[()]+$/g, '');
      const style = STRENGTH_STYLES[word];
      if (style) {
        try {
          text.format(offset, len, style as Record<string, unknown>);
        } catch {
          /* ignore — happens during concurrent CRDT updates */
        }
      }
    }
    offset += len;
  }
}

/**
 * Highlight Verified / Preprint / Retracted badge words anywhere they
 * appear. These almost only occur in the Sources block we build, and
 * the false-positive risk is acceptable (a stray "verified" in prose
 * gets a green tint — still readable).
 */
function highlightStatusBadges(text: YText): void {
  const plain = text.toString();
  BADGE_PATTERN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = BADGE_PATTERN.exec(plain)) !== null) {
    const word = m[1].toLowerCase();
    const style = STATUS_BADGE_STYLES[word];
    if (!style) continue;
    try {
      text.format(m.index, m[0].length, style as Record<string, unknown>);
    } catch {
      /* ignore */
    }
  }
}

export function applyResearchHighlights(
  doc: Store,
  blockIds: ReadonlyArray<string>,
  runWith: (fn: () => void) => void
): void {
  if (!blockIds.length) return;
  runWith(() => {
    for (const id of blockIds) {
      const model = doc.getBlock(id)?.model as BlockLike | undefined;
      if (!model) continue;
      walk(model, m => {
        if (!m.text) return;
        highlightEvidenceGrades(m.text);
        highlightStatusBadges(m.text);
      });
    }
  });
}
